import { useEffect, useState, useCallback } from 'react'
import { Command } from 'cmdk'
import { useNavigate } from 'react-router-dom'
import { FiSearch } from 'react-icons/fi'
import { useCommandPaletteItems } from './useCommandPaletteItems'
import type { PaletteItem } from './useCommandPaletteItems'

export function CommandPalette() {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const items = useCommandPaletteItems()

  // Global Cmd+K / Ctrl+K listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const handleSelect = useCallback(
    (item: PaletteItem) => {
      setOpen(false)
      if (item.action.type === 'navigate') {
        navigate(item.action.path)
      } else {
        item.action.fn()
      }
    },
    [navigate]
  )

  // Group items
  const groups = ['Pages', 'Challenges', 'Communities', 'Actions', 'Users'] as const
  const groupedItems = groups.reduce(
    (acc, group) => {
      const groupItems = items.filter((i) => i.group === group)
      if (groupItems.length > 0) acc[group] = groupItems
      return acc
    },
    {} as Record<string, PaletteItem[]>
  )

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label="Command palette"
      loop
      className="fixed inset-0 z-[9999]"
    >
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50" />

      {/* Panel */}
      <div className="fixed inset-0 flex items-start justify-center pt-[20vh]">
        <div className="w-full max-w-xl rounded-[var(--radius-card)] border border-border bg-background shadow-lg overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-2 border-b border-border px-4">
            <FiSearch className="text-foreground-muted shrink-0" size={16} />
            <Command.Input
              placeholder="Search pages, challenges, communities..."
              className="flex h-12 w-full bg-transparent text-sm text-foreground placeholder:text-foreground-muted focus:outline-none"
            />
            <kbd className="hidden sm:inline-flex items-center gap-1 rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-foreground-muted">
              Esc
            </kbd>
          </div>

          {/* Results list */}
          <Command.List className="max-h-[min(60vh,400px)] overflow-y-auto p-2">
            <Command.Empty className="py-8 text-center text-sm text-foreground-muted">
              No results found.
            </Command.Empty>

            {Object.entries(groupedItems).map(([group, groupItems]) => (
              <Command.Group
                key={group}
                heading={group}
                className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-foreground-muted"
              >
                {groupItems.map((item) => (
                  <Command.Item
                    key={item.id}
                    value={`${item.label} ${item.description ?? ''}`}
                    onSelect={() => handleSelect(item)}
                    className="flex items-center gap-3 rounded-[var(--radius-button)] px-2 py-2 text-sm text-foreground cursor-pointer aria-selected:bg-primary/10 aria-selected:text-primary"
                  >
                    <item.icon className="shrink-0 opacity-60" size={16} />
                    <div className="flex-1 min-w-0">
                      <span className="block truncate">{item.label}</span>
                      {item.description && (
                        <span className="block truncate text-xs text-foreground-muted">
                          {item.description}
                        </span>
                      )}
                    </div>
                  </Command.Item>
                ))}
              </Command.Group>
            ))}
          </Command.List>

          {/* Footer */}
          <div className="flex items-center gap-4 border-t border-border px-4 py-2 text-[11px] text-foreground-muted">
            <span>
              <kbd className="rounded border border-border bg-muted px-1 py-0.5 font-mono">↑↓</kbd>{' '}
              Navigate
            </span>
            <span>
              <kbd className="rounded border border-border bg-muted px-1 py-0.5 font-mono">↵</kbd>{' '}
              Select
            </span>
            <span>
              <kbd className="rounded border border-border bg-muted px-1 py-0.5 font-mono">Esc</kbd>{' '}
              Close
            </span>
          </div>
        </div>
      </div>
    </Command.Dialog>
  )
}
